import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X, Star, Megaphone } from 'lucide-react';
import api from '@/services/apiClient';
import PageHeader from '@/components/ui/PageHeader';
import DataTable from '@/components/ui/DataTable';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import Toolbar from '@/components/ui/Toolbar';
import { PACKAGE_STATUS, fmtMoney, fmtNum } from '@/lib/enums';

const STATUS_FILTER = (t) => [
  { value: '', label: t('marketplace.filter.all') },
  { value: 'pending', label: t('marketplace.filter.pending') },
  { value: 'published', label: t('marketplace.filter.published') },
  { value: 'rejected', label: t('marketplace.filter.rejected') },
  { value: 'draft', label: t('marketplace.filter.draft') },
];

// Período padrão de um novo patrocínio: 30 dias a partir de agora.
const sponsorshipPeriod = () => {
  const now = new Date();
  const end = new Date(now);
  end.setDate(end.getDate() + 30);
  return { startsAt: now.toISOString(), endsAt: end.toISOString() };
};

export default function AdminMarketplace() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  // Map packageId -> sponsorship ativo (para saber se já está patrocinado).
  const [sponsoredMap, setSponsoredMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/packages/admin/all', status ? { status } : {})
        .then((res) => (res.data || res).packages || res.data || [])
        .catch(() => []),
      api.get('/sponsorships')
        .then((res) => (res.data || res).sponsorships || [])
        .catch(() => []),
    ])
      .then(([pkgs, sponsorships]) => {
        setRows(pkgs);
        // Considera apenas os patrocínios ativos para o toggle.
        const map = {};
        for (const s of sponsorships) {
          if (s.active) map[s.packageId] = s.id;
        }
        setSponsoredMap(map);
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, [status]);

  const setStatusOf = async (id, next) => {
    setBusy(id + next);
    try {
      await api.patch(`/packages/admin/${id}/status`, { status: next });
      load();
    } catch (e) {
      alert(e.message);
    } finally {
      setBusy(null);
    }
  };

  const toggleFeatured = async (r) => {
    setBusy(r.id + 'feat');
    try {
      await api.post(`/packages/admin/${r.id}/featured`, { isFeatured: !r.isFeatured });
      load();
    } catch (e) {
      alert(e.message);
    } finally {
      setBusy(null);
    }
  };

  // Cria ou remove o patrocínio (destaque pago) do pacote.
  const toggleSponsored = async (r) => {
    setBusy(r.id + 'spon');
    try {
      const current = sponsoredMap[r.id];
      if (current) {
        // Desativa o patrocínio ativo.
        await api.put(`/sponsorships/${current}`, { active: false });
      } else {
        await api.post('/sponsorships', { packageId: r.id, ...sponsorshipPeriod() });
      }
      load();
    } catch (e) {
      alert(e.message);
    } finally {
      setBusy(null);
    }
  };

  const columns = [
    {
      key: 'title',
      header: t('marketplace.column.package'),
      render: (r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {r.isFeatured && <Star size={14} fill="var(--warning)" color="var(--warning)" />}
          <span style={{ fontWeight: 600 }}>{r.title || r.name}</span>
          {sponsoredMap[r.id] && <Badge variant="accent">{t('marketplace.sponsored')}</Badge>}
        </div>
      ),
    },
    { key: 'trainer', header: t('marketplace.column.trainer'), render: (r) => r.trainer?.name || r.trainerName || '—' },
    { key: 'price', header: t('marketplace.column.price'), render: (r) => fmtMoney(r.price) },
    { key: 'sales', header: t('marketplace.column.sales'), render: (r) => fmtNum(r.sales ?? r.salesCount) },
    {
      key: 'status',
      header: t('screens.status'),
      render: (r) => {
        const s = PACKAGE_STATUS[r.status] || { label: r.status, variant: 'neutral' };
        return <Badge variant={s.variant}>{s.label}</Badge>;
      },
    },
    {
      key: 'actions',
      header: t('screens.actions'),
      align: 'right',
      render: (r) => {
        const isSponsored = !!sponsoredMap[r.id];
        const featureLabel = r.isFeatured ? t('marketplace.unfeature') : t('screens.feature');
        const sponsorLabel = isSponsored ? t('marketplace.unsponsor') : t('marketplace.sponsor');
        // Só ícone + tooltip (padrão das tabelas) — rótulos longos quebravam a linha.
        return (
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', whiteSpace: 'nowrap' }}>
            {r.status !== 'published' && (
              <Button size="sm" variant="outline" icon={Check} loading={busy === r.id + 'published'}
                title={t('screens.approve')} aria-label={t('screens.approve')}
                onClick={() => setStatusOf(r.id, 'published')} />
            )}
            {r.status !== 'rejected' && (
              <Button size="sm" variant="danger" icon={X} loading={busy === r.id + 'rejected'}
                title={t('screens.reject')} aria-label={t('screens.reject')}
                onClick={() => setStatusOf(r.id, 'rejected')} />
            )}
            <Button size="sm" variant="ghost" loading={busy === r.id + 'feat'}
              icon={<Star size={18} fill={r.isFeatured ? 'var(--warning)' : 'none'} aria-hidden />}
              title={featureLabel} aria-label={featureLabel}
              onClick={() => toggleFeatured(r)} />
            <Button size="sm" variant="ghost" loading={busy === r.id + 'spon'}
              icon={<Megaphone size={18} color={isSponsored ? 'var(--volt)' : 'currentColor'} aria-hidden />}
              title={sponsorLabel} aria-label={sponsorLabel}
              onClick={() => toggleSponsored(r)} />
          </div>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader kicker="Movez · Admin" title={t('nav.marketplace')} />
      <Toolbar>
        <Select value={status} onChange={(e) => setStatus(e.target.value)} options={STATUS_FILTER(t)} />
      </Toolbar>
      <DataTable columns={columns} rows={rows} loading={loading} />
    </>
  );
}
